import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { WATER_BOTTLE_ML, WATER_GLASS_ML, WATER_MAX_ENTRY_ML, waterDueBy } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalWaterEntry } from '../../db/local.js';
import { readProfile } from '../../db/profile.js';
import { addWater, listWater, undoLastWater } from '../../db/water.js';
import { toNumber } from './FoodForm.js';
import { WaterTargetField } from './WaterSetting.js';
import { useRememberedOpen } from '../../hooks/useRememberedOpen.js';

const WATER_OPEN_KEY = 'kraftwerk:water-open';

/**
 * Wasser im Tag (18.10.2026): Stand gegen Ziel, ein Tipp für ein Glas oder
 * eine Flasche, „Rückgängig“ für den Fehltipp. Heute steht dazu, wie viel
 * bis jetzt etwa dran wäre — statt einer Erinnerung, die nervt.
 *
 * Seit 20.10.2026 eine eigene Kachel, einklappbar (je Gerät gemerkt): Zu
 * bleiben Stand und Balken sichtbar. Das Tagesziel lässt sich hier ändern —
 * vorher stand es nur unter Einstellungen → Erinnerungen, wo es keiner
 * suchte. Dasselbe Feld wie dort (`WaterTargetField`), kein zweiter Weg.
 */
export function WaterTile({ date, today }: { date: string; today: string }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const entries = useLiveQuery(() => listWater(date, db), [date], [] as LocalWaterEntry[]);
  const target = useLiveQuery(async () => (await readProfile(db)).water.targetMl, [], null);
  const [open, setOpen] = useRememberedOpen(WATER_OPEN_KEY, true);
  const [other, setOther] = useState(false);
  const [editTarget, setEditTarget] = useState(false);
  const [amount, setAmount] = useState('');
  const id = useId();
  if (target === null) return <></>;

  const drunk = entries.reduce((sum, entry) => sum + entry.ml, 0);
  const share = Math.min(1, drunk / target) * 100;
  const liters = (ml: number) =>
    (ml / 1000).toLocaleString(i18n.language, { maximumFractionDigits: 2 });
  const now = new Date().toTimeString().slice(0, 5);
  const due = date === today ? waterDueBy(target, now) : null;

  return (
    <details
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      className="kw-tile"
    >
      <summary className="flex cursor-pointer list-none flex-col gap-2 [&::-webkit-details-marker]:hidden">
        <span className="flex items-baseline justify-between gap-2">
          <span className="kw-kicker">
            <span aria-hidden="true" className="mr-1 text-[0.8em]">
              {open ? '▼' : '▶'}
            </span>
            {t('water.heading')}
          </span>
          <span className="text-sm">
            <span className="kw-figure text-lg">{liters(drunk)}</span>
            <span className="text-xs text-ink-muted"> / {liters(target)} l</span>
          </span>
        </span>
        <span className="kw-bar kw-bar-thin" aria-hidden="true">
          <i className={drunk >= target ? 'bg-good' : ''} style={{ width: `${share}%` }} />
        </span>
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        {due !== null && drunk < target ? (
          <p className="text-xs text-ink-muted">
            {drunk >= due ? t('water.onTrack') : t('water.dueBy', { ml: due - drunk })}
          </p>
        ) : null}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void addWater(date, WATER_GLASS_ML, db)}
            aria-label={t('water.glassNamed', { ml: WATER_GLASS_ML })}
            className="kw-btn kw-btn-small flex-1 px-2"
          >
            {t('water.plus', { ml: WATER_GLASS_ML })}
          </button>
          <button
            type="button"
            onClick={() => void addWater(date, WATER_BOTTLE_ML, db)}
            aria-label={t('water.bottleNamed', { ml: WATER_BOTTLE_ML })}
            className="kw-btn kw-btn-small flex-1 px-2"
          >
            {t('water.plus', { ml: WATER_BOTTLE_ML })}
          </button>
          <button
            type="button"
            aria-expanded={other}
            onClick={() => setOther((value) => !value)}
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
          >
            {t('water.other')}
          </button>
          {entries.length > 0 ? (
            <button
              type="button"
              onClick={() => void undoLastWater(date, db)}
              aria-label={t('water.undoNamed', { ml: entries.at(-1)!.ml })}
              className="kw-btn kw-btn-quiet kw-btn-small px-3"
            >
              ↶
            </button>
          ) : null}
        </div>
        {other ? (
          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              const ml = toNumber(amount);
              if (ml === null || ml <= 0 || ml > WATER_MAX_ENTRY_ML) return;
              void addWater(date, ml, db).then(() => {
                setAmount('');
                setOther(false);
              });
            }}
          >
            <label htmlFor={id} className="kw-label flex w-28 flex-col gap-1.5">
              {t('water.amount')}
              <input
                id={id}
                inputMode="numeric"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                className="kw-field"
              />
            </label>
            <button type="submit" className="kw-btn kw-btn-small flex-1">
              {t('water.add')}
            </button>
          </form>
        ) : null}
        <button
          type="button"
          aria-expanded={editTarget}
          onClick={() => setEditTarget((value) => !value)}
          className="self-start text-xs text-ink-muted underline underline-offset-2"
        >
          {t('water.changeTarget')}
        </button>
        {editTarget ? <WaterTargetField onSaved={() => setEditTarget(false)} /> : null}
      </div>
    </details>
  );
}
